import { useState, useEffect } from 'react'
import { Shield, AlertTriangle, CheckCircle, Clock, FileText, Calendar } from 'lucide-react'

interface ComplianceRecord {
  id: string
  client_id: string
  client_name: string
  client_industry: string
  compliance_type: string
  description: string
  status: string
  priority: string
  due_date: string
  completion_date: string | null
  assigned_to: string
  assigned_to_name: string
  notes: string
}

interface Summary {
  total_records: number
  pending: number
  in_progress: number
  completed: number
  overdue: number
}

export default function CompliancePage() {
  const [records, setRecords] = useState<ComplianceRecord[]>([])
  const [summary, setSummary] = useState<Summary | null>(null)
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState<string>('')

  useEffect(() => {
    fetchCompliance()
  }, [statusFilter])

  const fetchCompliance = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (statusFilter) params.append('status', statusFilter)

      const response = await fetch(`/api/compliance?${params.toString()}`)
      const data = await response.json()
      
      if (data.success) {
        setRecords(data.data)
        setSummary(data.summary)
      }
    } catch (error) {
      console.error('Error fetching compliance records:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100'
      case 'in_progress': return 'text-blue-600 bg-blue-100'
      case 'pending': return 'text-yellow-600 bg-yellow-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-100'
      case 'medium': return 'text-yellow-600 bg-yellow-100'
      case 'low': return 'text-green-600 bg-green-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const isOverdue = (record: ComplianceRecord) => {
    return record.status !== 'completed' && new Date(record.due_date) < new Date()
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getDaysUntilDue = (dueDate: string) => {
    const days = Math.ceil((new Date(dueDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
    if (days < 0) return `${Math.abs(days)} days overdue`
    if (days === 0) return 'Due today'
    return `${days} days remaining`
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-royal-blue"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-dashboard-title text-royal-blue-700 mb-2">
          Compliance & Risk Management
        </h1>
        <p className="text-graphite-grey-500">
          Monitor regulatory compliance requirements and track completion status
        </p>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-5 h-5 text-royal-blue" />
              <p className="text-sm text-graphite-grey">Total</p>
            </div>
            <p className="text-3xl font-bold text-royal-blue">{summary.total_records}</p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center gap-3 mb-2">
              <Clock className="w-5 h-5 text-yellow-600" />
              <p className="text-sm text-graphite-grey">Pending</p>
            </div>
            <p className="text-3xl font-bold text-yellow-600">{summary.pending}</p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center gap-3 mb-2">
              <Shield className="w-5 h-5 text-blue-600" />
              <p className="text-sm text-graphite-grey">In Progress</p>
            </div>
            <p className="text-3xl font-bold text-blue-600">{summary.in_progress}</p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <p className="text-sm text-graphite-grey">Completed</p>
            </div>
            <p className="text-3xl font-bold text-green-600">{summary.completed}</p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center gap-3 mb-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <p className="text-sm text-graphite-grey">Overdue</p>
            </div>
            <p className="text-3xl font-bold text-red-600">{summary.overdue}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex gap-2">
          <button
            onClick={() => setStatusFilter('')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              statusFilter === '' 
                ? 'bg-royal-blue text-white' 
                : 'bg-gray-100 text-graphite-grey hover:bg-gray-200'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setStatusFilter('pending')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              statusFilter === 'pending' 
                ? 'bg-yellow-600 text-white' 
                : 'bg-gray-100 text-graphite-grey hover:bg-gray-200'
            }`}
          >
            Pending
          </button>
          <button
            onClick={() => setStatusFilter('in_progress')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              statusFilter === 'in_progress' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-graphite-grey hover:bg-gray-200'
            }`}
          >
            In Progress
          </button>
          <button
            onClick={() => setStatusFilter('completed')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              statusFilter === 'completed' 
                ? 'bg-green-600 text-white' 
                : 'bg-gray-100 text-graphite-grey hover:bg-gray-200'
            }`}
          >
            Completed
          </button>
        </div>
      </div>

      {/* Compliance Records Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-semibold text-graphite-grey">Client</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-graphite-grey">Type</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-graphite-grey">Description</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-graphite-grey">Status</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-graphite-grey">Priority</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-graphite-grey">Due Date</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-graphite-grey">Assigned To</th>
              </tr>
            </thead>
            <tbody>
              {records.map((record) => (
                <tr 
                  key={record.id} 
                  className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                >
                  <td className="py-4 px-4">
                    <div>
                      <p className="font-medium text-vampire-black">{record.client_name}</p>
                      <p className="text-xs text-graphite-grey">{record.client_industry}</p>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-sm text-vampire-black">{record.compliance_type}</span>
                  </td>
                  <td className="py-4 px-4">
                    <p className="text-sm text-vampire-black max-w-xs truncate">{record.description}</p>
                  </td>
                  <td className="py-4 px-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(record.status)}`}>
                      {record.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPriorityColor(record.priority)}`}>
                      {record.priority}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <div className={isOverdue(record) ? 'text-red-600' : 'text-vampire-black'}>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <div>
                          <p className="text-sm font-medium">{formatDate(record.due_date)}</p>
                          <p className="text-xs">{getDaysUntilDue(record.due_date)}</p>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-sm text-vampire-black">{record.assigned_to_name}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Empty State */}
      {records.length === 0 && (
        <div className="bg-white p-12 rounded-lg border border-gray-200 text-center">
          <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-graphite-grey">No compliance records found</p>
        </div>
      )}
    </div>
  )
}
